import { agreeTermsSchema, type AgreeTerms } from '@/models/api/agree-terms'
import type { CompanyEmployeeExport } from '@/models/api/company-employee-export'
import type { CompanyEmployeeQueryData } from '@/models/api/company-employee-query-data'
import type { CompanyEmployeeUpdateData } from '@/models/api/company-employee-update-data'
import type { GetInvitationListByEmailRequest } from '@/models/api/get-invitation-list-by-email-request'
import type { GetInvitationListRequest } from '@/models/api/get-invitation-list-request'
import type { GetMemberInformationRequest } from '@/models/api/get-member-information-request'
import {
  invitationByEmailSchema,
  type InvitationByEmail
} from '@/models/api/invitation-by-email'
import {
  invitationRecordSchema,
  type InvitationRecord
} from '@/models/api/invitation-record'
import { memberSchema, type Member } from '@/models/api/member'
import type { MemberRequest } from '@/models/api/member-request'
import {
  sendInvitationByEmailSchema,
  type SendInvitationByEmail
} from '@/models/api/send-invitation-by-email'
import type { SendInvitationByEmailByIdentifierRequest } from '@/models/api/send-invitation-by-email-by-identifier-request'
import type { SendInvitationData } from '@/models/api/send-invitation-data'
import {
  companyEmployeeSchema,
  type CompanyEmployee,
  type CompanyEmployeeAnalyzeEmail
} from '~library/models'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class CompanyEmployeeApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得企業員工清單
   * https://hackmd.io/8EuA7tLpT4yfqOP_LCXbFA
   */
  @apiValidator(companyEmployeeSchema.array())
  getList(
    query?: CompanyEmployeeQueryData,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<CompanyEmployee[]>> {
    return this.api.post('CompanyEmployee/GetList', query, config)
  }

  /**
   * API 修改企業員工資料
   * https://hackmd.io/XwWDzy3OQdWZdUzPMG6iqw
   */
  update(
    data: CompanyEmployeeUpdateData,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.updateData('CompanyEmployee/CompanyEmployee', data, config)
  }

  /**
   * API 刪除企業員工資料
   * https://hackmd.io/EDcMExYlTLeMMg27QlT6jg
   */
  delete(data: { id: string[] }): Promise<ApiResponse> {
    return this.api.post('CompanyEmployee/DeleteCompanyEmployee', data)
  }

  /**
   * API 企業員工清單匯出
   * https://hackmd.io/geuNpl2cTgOABwczaZc-BA
   */
  export(data: CompanyEmployeeExport): Promise<void> {
    return this.api.downloadFile({
      method: 'POST',
      url: 'CompanyEmployee/Export',
      data: data
    })
  }

  /**
   * API 解析欲綁定企業會員名單
   * https://hackmd.io/tqeaxWJ-Q6aGnwK99ylsLA
   */
  analyzeEmailByExcel(
    data: File
  ): Promise<ApiResponse<CompanyEmployeeAnalyzeEmail>> {
    const formData = new FormData()

    formData.append('file', data)

    return this.api.post('CompanyEmployee/AnalyzeEmailByExcel', formData)
  }

  /**
   * API 取得已寄送邀請信的紀錄
   * https://hackmd.io/-DGrqxSRSPOPv3_e_pHFCg
   */
  @apiValidator(invitationRecordSchema.array())
  getInvitationList(
    data: GetInvitationListRequest
  ): Promise<ApiResponse<InvitationRecord[]>> {
    return this.api.post('CompanyEmployee/GetInvitationList', data)
  }

  /**
   * API 依據信箱查詢已寄送邀請信的紀錄
   * https://hackmd.io/iFI29coQQc2BpUkkpaKkAw
   */
  @apiValidator(invitationByEmailSchema.array())
  getInvitationListByEmail(
    data: GetInvitationListByEmailRequest
  ): Promise<ApiResponse<InvitationByEmail[]>> {
    return this.api.post('CompanyEmployee/GetInvitationListBySearch', data)
  }

  /**
   * API 寄送綁定企業會員邀請信
   * https://hackmd.io/n6k6TNe3TgiAIzJe9kXbRg
   */
  sendInvitationByEmail(data: SendInvitationData): Promise<ApiResponse> {
    return this.api.post('CompanyEmployee/SendInvitationByEmail', data)
  }

  /**
   * API 取得差旅人資訊
   * https://hackmd.io/haLe-UWDT_izEQv-hN6jcQ
   */
  @apiValidator(memberSchema.nullable())
  getMember(query: MemberRequest): Promise<ApiResponse<Member | null>> {
    return this.api.post('CompanyEmployee/GetMember', query, {
      headers: {
        ignoreRtnCodeError: ['60']
      }
    })
  }

  /**
   * API 取得企業會員同意條款資訊
   * https://hackmd.io/d8Xi1IKnRmqHPSjk1sacTg
   */
  @apiValidator(agreeTermsSchema)
  getAgreeTerms(): Promise<ApiResponse<AgreeTerms>> {
    return this.api.get('CompanyEmployee/GetAgreeTerms')
  }
  /**
   * API 企業會員同意條款
   * https://hackmd.io/DtsB6yfQRG2Syko11BWErw
   */
  updateAgreeTerms(): Promise<ApiResponse> {
    return this.api.put('CompanyEmployee/AgreeTerms', {})
  }

  /**
   * API 查詢企業員工資料
   * https://hackmd.io/QWC_mg4hSkmawePzf8vIbw
   */
  @apiValidator(companyEmployeeSchema.array())
  getMemberInformation(
    data: GetMemberInformationRequest
  ): Promise<ApiResponse<CompanyEmployee[]>> {
    return this.api.post('CompanyEmployee/GetMemberInformation', data)
  }

  /**
   * API 寄送綁定企業會員邀請信by識別碼
   * https://hackmd.io/D3-Ft0-zRuOGtFEAqI9Thg
   */
  @apiValidator(sendInvitationByEmailSchema.nullable())
  sendInvitationByEmailByIdentifier(
    data: SendInvitationByEmailByIdentifierRequest
  ): Promise<ApiResponse<SendInvitationByEmail | null>> {
    return this.api.post(
      'CompanyEmployee/SendInvitationByEmailByIdentifier	',
      data
    )
  }
}
